import { Helmet } from 'react-helmet-async';

interface CourseSchemaProps {
  title: string;
  description: string;
  durationHours: number;
  priceHT?: number;
  priceTTC?: number;
  objectives?: string[];
  targetAudience?: string;
  prerequisites?: string;
  url: string;
  locale: string;
  sessions?: {
    id: string;
    start_date: string;
    end_date: string;
    location?: string;
    mode: 'online' | 'in-person' | 'hybrid';
    available_spots?: number;
  }[];
}

/**
 * Composant Schema.org Course pour rich snippets Google
 * https://schema.org/Course
 */
export function CourseSchema({
  title,
  description,
  durationHours,
  priceHT,
  priceTTC,
  objectives,
  targetAudience,
  prerequisites,
  url,
  locale,
  sessions,
}: CourseSchemaProps) {
  // Convertir la durée en format ISO 8601 (ex: "PT16H" pour 16 heures)
  const timeRequired = `PT${durationHours}H`;

  // Créer les instances de cours si des sessions sont disponibles
  const courseInstances = sessions?.map((session) => ({
    '@type': 'CourseInstance',
    courseMode: session.mode === 'online' ? 'https://schema.org/OnlineEventAttendanceMode' :
                session.mode === 'in-person' ? 'https://schema.org/OfflineEventAttendanceMode' :
                'https://schema.org/MixedEventAttendanceMode',
    startDate: session.start_date,
    endDate: session.end_date,
    ...(session.location && { location: session.location }),
    inLanguage: locale,
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: title,
    description,
    provider: {
      '@type': 'Organization',
      name: 'Aspexit',
      url: 'https://aspexit.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://aspexit.com/logo.png',
      },
      sameAs: [
        'https://www.linkedin.com/in/corentin-leroux-0225a182/',
        'https://twitter.com/CP_Leroux',
      ],
    },
    instructor: {
      '@type': 'Person',
      name: 'Corentin Leroux',
      jobTitle: 'Expert en Numérique Agricole',
      url: 'https://www.linkedin.com/in/corentin-leroux-0225a182/',
      sameAs: [
        'https://www.linkedin.com/in/corentin-leroux-0225a182/',
        'https://twitter.com/CP_Leroux',
      ],
    },
    ...(objectives && objectives.length > 0 && {
      teaches: objectives.join(', '),
    }),
    ...(targetAudience && {
      audience: {
        '@type': 'EducationalAudience',
        audienceType: targetAudience,
      },
    }),
    ...(prerequisites && {
      coursePrerequisites: prerequisites,
    }),
    timeRequired,
    inLanguage: locale,
    url,
    ...(courseInstances && courseInstances.length > 0 && {
      hasCourseInstance: courseInstances,
    }),
    ...((priceTTC || priceHT) && {
      offers: {
        '@type': 'Offer',
        price: priceTTC || priceHT,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url,
        ...(priceTTC && priceHT && {
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: priceTTC,
            priceCurrency: 'EUR',
            valueAddedTaxIncluded: 'True',
          },
        }),
      },
    }),
    educationalCredentialAwarded: {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'Attestation de formation',
      recognizedBy: {
        '@type': 'Organization',
        name: 'Aspexit',
      },
    },
    hasCertification: {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'Qualiopi',
      name: 'Certification Qualiopi - Organisme de formation certifié',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
